import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Renderer2
} from '@angular/core';
import { computePosition, flip, shift, offset, autoUpdate, Placement } from '@floating-ui/dom';

/**
 * Directiva de tooltip basada en @floating-ui/dom.
 *
 * Reemplaza el sistema legacy [data-tooltip]::before global. El tooltip se
 * renderiza en document.body como hijo directo, lo que lo libera de cualquier
 * stacking context, overflow:hidden o backdrop-filter que pudiera tener un
 * contenedor ancestro.
 *
 * Uso típico:
 *   <span [appTooltip]="'Texto explicativo'">Hover aquí</span>
 *
 * Con posición personalizada:
 *   <button [appTooltip]="'Aviso'" appTooltipPlacement="bottom">Click</button>
 *
 * Si el texto es vacío o null, el tooltip no se muestra (fail-safe).
 */
@Directive({
  selector: '[appTooltip]'
})
export class TooltipDirective implements OnDestroy {

  /** Texto a mostrar en el tooltip. Si vacío o null, no se renderiza. */
  @Input('appTooltip') text: string | null | undefined;

  /** Lado preferente del tooltip respecto al trigger. Default: 'top'. */
  @Input('appTooltipPlacement') placement: Placement = 'top';

  /** Delay en ms antes de mostrar (evita parpadeos al pasar rápido). */
  @Input('appTooltipDelay') delay: number = 150;

  private tooltipEl: HTMLElement | null = null;
  private cleanupAutoUpdate: (() => void) | null = null;
  private showTimer: any = null;

  constructor(
    private host: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {}

  // ==========================================================================
  // Eventos del trigger
  // ==========================================================================

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.scheduleShow();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.hide();
  }

  @HostListener('focus')
  onFocus(): void {
    // Mostramos inmediatamente al focus (accesibilidad teclado).
    this.show();
  }

  @HostListener('blur')
  onBlur(): void {
    this.hide();
  }

  /**
   * Cuando se hace click en el trigger, el cursor sale, etc., aseguramos
   * que el tooltip queda oculto. Defensa frente a estados huérfanos.
   */
  @HostListener('click')
  onClick(): void {
    this.hide();
  }

  // ==========================================================================
  // Ciclo de vida
  // ==========================================================================

  ngOnDestroy(): void {
    this.hide();
  }

  // ==========================================================================
  // Mostrar / ocultar / posicionar
  // ==========================================================================

  private scheduleShow(): void {
    this.clearShowTimer();
    if (!this.hasText()) return;
    this.showTimer = setTimeout(() => this.show(), this.delay);
  }

  private show(): void {
    this.clearShowTimer();
    if (!this.hasText()) return;
    if (this.tooltipEl) return; // Ya visible.

    // Construir nodo del tooltip.
    this.tooltipEl = this.renderer.createElement('div');
    this.renderer.addClass(this.tooltipEl, 'app-tooltip');
    this.renderer.setAttribute(this.tooltipEl!, 'role', 'tooltip');
    this.tooltipEl!.textContent = this.text!;

    // Insertar en body para liberarlo de stacking contexts.
    this.renderer.appendChild(document.body, this.tooltipEl);

    // Posicionar y mantener actualizado mientras el tooltip esté visible.
    this.cleanupAutoUpdate = autoUpdate(
      this.host.nativeElement,
      this.tooltipEl!,
      () => this.updatePosition()
    );
  }

  private hide(): void {
    this.clearShowTimer();
    if (this.cleanupAutoUpdate) {
      this.cleanupAutoUpdate();
      this.cleanupAutoUpdate = null;
    }
    if (this.tooltipEl && this.tooltipEl.parentNode) {
      this.renderer.removeChild(this.tooltipEl.parentNode, this.tooltipEl);
    }
    this.tooltipEl = null;
  }

  private async updatePosition(): Promise<void> {
    if (!this.tooltipEl) return;

    const result = await computePosition(this.host.nativeElement, this.tooltipEl, {
      placement: this.placement,
      middleware: [
        offset(10),                       // 10px de separación del trigger
        flip(),                           // Cambia de lado si no cabe (top → bottom, etc.)
        shift({ padding: 8 })             // Desplaza horizontalmente para evitar overflow del viewport
      ]
    });

    // Aplicar posición computada.
    Object.assign(this.tooltipEl.style, {
      left: `${result.x}px`,
      top: `${result.y}px`
    });
  }

  private clearShowTimer(): void {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  private hasText(): boolean {
    return typeof this.text === 'string' && this.text.trim().length > 0;
  }
}